/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import window from 'ember-window-mock';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CampaignModel from 'cio/models/campaign';
import { FlyActionDropdownItem } from '@customerio/fly-components/addon/components/fly-action-dropdown';
import SegmentModel from 'cio/models/segment';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import RouterService from '@ember/routing/router-service';
import CustomFormsService from 'cio/services/custom-forms';
import CustomForm from 'cio/models/custom-form';
import { isFacebookForm, isCustomForm, FormType, formDetailsPageModels } from 'cio/utils/forms';
import EnvironmentService from 'cio/services/environment';
import { task } from 'ember-concurrency';
import joinWords from 'cio/utils/join-words';
import AuthService from 'cio/services/auth';

interface FormsFormsTableRowArgs {
  form: FacebookLeadAdsForm | CustomForm;
  segment: SegmentModel;
  campaigns: CampaignModel[];

  connectAction: (f: FacebookLeadAdsForm | CustomForm, type: 'facebook' | 'custom') => unknown;
  disconnectAction: (f: FacebookLeadAdsForm | CustomForm, type: 'facebook' | 'custom') => unknown;

  showFormsEndpointAction: () => unknown;
  showJSSnippetAction: () => unknown;
  showHTMLAttributeAction: () => unknown;
  showTypeformAction: () => unknown;
  showJotformAction: () => unknown;
}

export default class FormsFormsTableRow extends Component<FormsFormsTableRowArgs> {
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service router!: RouterService;
  @service customForms!: CustomFormsService;

  get isFacebookForm() {
    return isFacebookForm(this.args.form);
  }

  get isCustomForm() {
    return !this.isFacebookForm;
  }

  get availableIdentifiers() {
    return joinWords(this.environment.current.enabledIdentifiers, ' ', ' or ');
  }

  get actionOptions() {
    let { form, connectAction } = this.args;

    let items: FlyActionDropdownItem[] = [];

    if (form.isDisconnected) {
      items.push({
        label: 'Connect form',
        action: () => connectAction(form, this.isFacebookForm ? 'facebook' : 'custom'),
        disabled: !this.auth.hasPermission('journeys/forms/create') || (isFacebookForm(form) && form.isArchived),
        tooltip: {
          condition: isFacebookForm(form) && form.isArchived,
          content: 'This form is archived in Facebook so you cannot reconnect it to Customer.io.',
        },
      });
    }

    if (form.isConnected || (isCustomForm(form) && form.isDraft)) {
      items.push({
        label: 'Manage form',
        action: () => this.goToFormDetails(),
      });
    }

    if (isFacebookForm(form)) {
      items.push({
        label: 'Test in Facebook',
        action: this.openLeadAdsTestTool,
      });
    }

    if (isCustomForm(form)) {
      if (form.isApiForm) {
        items.push({
          label: 'Show API endpoint',
          action: this.args.showFormsEndpointAction,
        });
      } else if (form.isScannedForm) {
        items.push({
          label: 'Show JS snippet',
          action: this.args.showJSSnippetAction,
        });
      } else if (form.isHTMLActionForm) {
        items.push({
          label: 'Show form action attribute',
          action: this.args.showHTMLAttributeAction,
          classNames: 'whitespace-nowrap', // force to be on a single-line: otherwise looks ugly
        });
      } else if (form.isTypeform) {
        items.push({
          label: 'Show Typeform details',
          action: this.args.showTypeformAction,
        });
      } else if (form.isJotform) {
        items.push({
          label: 'Show Jotform details',
          action: this.args.showJotformAction,
        });
      }
    }

    if (form.isConnected || (isCustomForm(form) && form.isDraft)) {
      items.push({
        label: 'Disconnect form',
        classNames: 'text-error',
        disabled: !this.auth.hasPermission('journeys/forms/delete'),
        action: () => this.args.disconnectAction(form, this.isFacebookForm ? 'facebook' : 'custom'),
      });
    }

    return items;
  }

  get bannerTitle() {
    let form = this.args.form as CustomForm;

    if (form.hasErrMissingIdentifier) {
      return `A valid ${this.availableIdentifiers} was not found`;
    } else if (form.hasErrMultipleIdentifiers) {
      return 'Multiple identifiers were found';
    } else if (form.hasErrInvalidEmailIdentifier) {
      return 'Invalid email identifier was found';
    } else if (form.isNewForm) {
      if (form.formType === FormType.Custom) {
        return 'This form was submitted directly to the endpoint';
      } else if (form.formType === FormType.Typeform) {
        return 'This form was submitted via webhook from Typeform';
      }
    }

    return;
  }

  dismissNewFormBannerTask = task({ drop: true }, async () => {
    // do a save on the form without any changes, which will update internal "updatedAt" timestamp to be different than "createdAt":
    await this.args.form.save();
  });

  @action
  goToFormDetails() {
    this.router.transitionTo('integrations.forms.form_details', ...formDetailsPageModels(this.args.form));
  }

  @action
  openLeadAdsTestTool() {
    this.analytics.track('events', 'send_started', { source: 'FB Lead Ads Test' });

    window.open('https://developers.facebook.com/tools/lead-ads-testing');
  }

  @action
  handleRowClick(e: MouseEvent) {
    // don't navigate if clicked on <a> or <button> elements: those have their own, separate actions
    let shouldIgnoreClick = (elem: HTMLElement) => ['A', 'BUTTON'].includes(elem.tagName);
    let shouldIgnoreTrack = (elem: HTMLElement) => elem.tagName === 'BUTTON'; // track clicking on a link to form details page, ignore only action buttons

    // try finding <button> among parent elements, several levels in
    // this is necessary to correctly handle clicks on <svg> icon and its nodes nested within <button> (<FlyActionDropdown> case)
    let target = e.target as HTMLElement;
    let i = 0;
    while (i < 5 && !shouldIgnoreClick(target)) {
      target = target.parentNode as HTMLElement;
      i++;
    }

    if (!shouldIgnoreTrack(target)) {
      this.analytics.track('forms', 'manage_form_started', {
        environmentId: this.environment.current.id,
        action: 'Managing',
        formType: this.args.form.formType,
        formId: this.args.form.formId,
        api: isCustomForm(this.args.form) && this.args.form.isApiForm,
        scanned: isCustomForm(this.args.form) && this.args.form.isScannedForm,
      });
    }

    if (shouldIgnoreClick(target)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    this.goToFormDetails();
  }
}
